import type { GhostwriterButtonAction } from '../context/useGhostwriterForViewJob';

import { type GhostwriterAutoApplyCta,
    ELEMENT_NAME, EVENT_PARAMETER, EVENT_TAG, MY_JOBS_SURFACE_TAGS,
    MYJOBS_AUTO_APPLY_CTA_ELEMENT_NAME, MYJOBS_AUTO_APPLY_SUB_TAB, SCREEN_NAME,
} from "./constants";
import { logScreenViewWithExtraParams } from '../../shared/native-modules/tare/logScreenViewWithExtraParams';
import { logTapButtonWithExtraParams } from '../../shared/native-modules/tare/logTapButtonWithExtraParams';

const DEFAULT_TAGS = [EVENT_TAG.GHOSTWRITER];

/** Primary CTA on a Ghostwriter Auto Apply job card in My Jobs; `elementName` is the button label. */
export function logGhostwriterAutoApplyCtaTap(jobKey: string, cta: GhostwriterAutoApplyCta): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_MYJOBS,
        MYJOBS_AUTO_APPLY_CTA_ELEMENT_NAME[cta],
        MY_JOBS_SURFACE_TAGS,
        {
            [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
            [EVENT_PARAMETER.VALUE]: cta,
        },
    );
}

/** Overflow (more) menu on a Ghostwriter Auto Apply job card → manage job. */
export function logGhostwriterAutoApplyOverflowMenuTap(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_MYJOBS,
        ELEMENT_NAME.MYJOBS_AUTO_APPLY_OVERFLOW_MENU,
        MY_JOBS_SURFACE_TAGS,
        {
            [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
        },
    );
}

export type GhostwriterAutoApplyStopDialogAction = 'confirm' | 'cancel';

/** Stop auto apply confirmation dialog actions. */
export function logGhostwriterAutoApplyStopDialogTap(
    jobKey: string,
    action: GhostwriterAutoApplyStopDialogAction,
): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_MYJOBS,
        ELEMENT_NAME.MYJOBS_AUTO_APPLY_STOP_DIALOG,
        MY_JOBS_SURFACE_TAGS,
        {
            [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
            [EVENT_PARAMETER.VALUE]: action,
        },
    );
}

/** Manage this job sheet: menu row (`action` = item id) or sheet dismissed (`close`). */
export function logGhostwriterManageJobMenuTap(jobKey: string, action: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_MYJOBS,
        ELEMENT_NAME.MYJOBS_AUTO_APPLY_MANAGE_JOB_MENU,
        MY_JOBS_SURFACE_TAGS,
        {
            [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
            [EVENT_PARAMETER.VALUE]: action,
        },
    );
}

export type GhostwriterUndoManageJobDialogAction = 'confirm' | 'cancel';

/** Undo auto apply confirmation alert from manage job sheet. */
export function logGhostwriterUndoManageJobDialogTap(
    jobKey: string,
    action: GhostwriterUndoManageJobDialogAction,
): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_MYJOBS,
        ELEMENT_NAME.MYJOBS_AUTO_APPLY_UNDO_MANAGE_JOB_DIALOG,
        MY_JOBS_SURFACE_TAGS,
        {
            [EVENT_PARAMETER.MYJOBS_SUB_TAB]: MYJOBS_AUTO_APPLY_SUB_TAB,
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
            [EVENT_PARAMETER.VALUE]: action,
        },
    );
}

export function logGhostwriterEntrypointImpression(jobKey: string): void {
    void logScreenViewWithExtraParams(SCREEN_NAME.RN_VIEW_JOB, DEFAULT_TAGS, {
        [EVENT_PARAMETER.JOB_KEY]: jobKey,
    });
}

export function logGhostwriterEntrypointTapFirstTime(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_VIEW_JOB, ELEMENT_NAME.ENTRYPOINT_FIRST_TIME,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterEntrypointTapReturning(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_VIEW_JOB, ELEMENT_NAME.ENTRYPOINT_RETURNING,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterConsentImpression(jobKey: string): void {
    void logScreenViewWithExtraParams(SCREEN_NAME.GHOSTWRITER_ONBOARDING, DEFAULT_TAGS, {
        [EVENT_PARAMETER.JOB_KEY]: jobKey,
    });
}

export function logGhostwriterConsentContinue(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_ONBOARDING, ELEMENT_NAME.CONSENT_CONTINUE,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterConsentDismiss(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_ONBOARDING, ELEMENT_NAME.CONSENT_DISMISS,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterReviewSaveAndClose(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_REVIEW, ELEMENT_NAME.REVIEW_SAVE_AND_CLOSE,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterReviewSubmit(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_REVIEW, ELEMENT_NAME.REVIEW_SUBMIT,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterReviewBack(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_REVIEW, ELEMENT_NAME.REVIEW_BACK,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterReviewClose(jobKey: string): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.GHOSTWRITER_REVIEW, ELEMENT_NAME.REVIEW_CLOSE,
        DEFAULT_TAGS, { [EVENT_PARAMETER.JOB_KEY]: jobKey },
    );
}

export function logGhostwriterThirdPartyApplyStart(
    jobKey: string,
    buttonAction: GhostwriterButtonAction,
): void {
    void logTapButtonWithExtraParams(
        SCREEN_NAME.RN_VIEW_JOB,
        ELEMENT_NAME.ENTRYPOINT_THIRD_PARTY_APPLY,
        DEFAULT_TAGS,
        {
            [EVENT_PARAMETER.JOB_KEY]: jobKey,
            [EVENT_PARAMETER.GHOSTWRITER_STATE]: buttonAction,
        },
    );
}
